import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Users, Package, Car, LineChart, Repeat } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const RELATION_TONE = {
    Self: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Spouse: "bg-rose-50 text-rose-700 border-rose-200",
    Child: "bg-amber-50 text-amber-800 border-amber-200",
    Parent: "bg-sky-50 text-sky-700 border-sky-200",
    Business: "bg-violet-50 text-violet-700 border-violet-200",
    Other: "bg-stone-50 text-stone-600 border-stone-200",
};

export default function Family() {
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(null);
    const [drill, setDrill] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get("/family/summary");
            setData(d);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const pickRelation = async (rel) => {
        setSelected(rel);
        try {
            const { data: d } = await api.get(`/family/${rel}`);
            setDrill(d);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!data) return <div className="text-stone-500 text-sm">Loading…</div>;

    return (
        <div data-testid="family-page">
            <div className="mb-6">
                <div className="eyebrow flex items-center gap-1.5"><Users size={12} /> Family Finance</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Household, member by member.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-xl">
                    See who owns what and who pays for what. Assets, vehicles and investments are
                    attributed by the &ldquo;owner&rdquo; field on each record, so nothing else changes &mdash; this is a
                    lens, not a new database.
                </p>
            </div>

            <div className="rounded-lg border border-stone-200 bg-white p-5 mb-6" data-testid="family-household-tile">
                <div className="eyebrow">Household total value</div>
                <div className="font-mono-figure text-4xl mt-1 text-stone-900">{fmtMoney(data.household_value)}</div>
                <div className="text-[11px] text-stone-500 mt-1">Assets + vehicles + investment current values, aggregated across every relation.</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
                {(data.members || []).map((m) => {
                    const isEmpty = m.assets_count + m.vehicles_count + m.investments_count === 0;
                    if (isEmpty) return null;
                    const tone = RELATION_TONE[m.owner] || RELATION_TONE.Other;
                    return (
                        <button
                            key={m.owner}
                            className={`text-left rounded-lg border p-4 hover:border-stone-300 transition-colors ${selected === m.owner ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"}`}
                            onClick={() => pickRelation(m.owner)}
                            data-testid={`family-tile-${m.owner}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${tone}`}>{m.owner}</span>
                                <span className="text-[11px] text-stone-500">{m.assets_count + m.vehicles_count + m.investments_count} records</span>
                            </div>
                            <div className="font-mono-figure text-2xl mt-2 text-stone-900">{fmtMoney(m.total_value)}</div>
                            <div className="mt-3 grid grid-cols-3 gap-1 text-[11px] text-stone-500">
                                <div><Package size={10} className="inline mr-0.5" />{m.assets_count} assets</div>
                                <div><Car size={10} className="inline mr-0.5" />{m.vehicles_count} vehicles</div>
                                <div><LineChart size={10} className="inline mr-0.5" />{m.investments_count} invest</div>
                            </div>
                            {m.subscriptions_monthly_estimate > 0 && (
                                <div className="text-[11px] text-stone-500 mt-1"><Repeat size={10} className="inline mr-0.5" />~ {fmtMoney(m.subscriptions_monthly_estimate)}/mo subs</div>
                            )}
                        </button>
                    );
                })}
                {(data.members || []).every((m) => m.assets_count + m.vehicles_count + m.investments_count === 0) && (
                    <div className="col-span-full rounded-lg border border-dashed border-stone-300 bg-white p-10 text-center text-stone-500 text-sm" data-testid="family-empty">
                        <Users size={20} className="mx-auto mb-2 text-stone-400" />
                        No family records yet &mdash; set the &ldquo;owner&rdquo; on assets, vehicles and investments to see them here.
                    </div>
                )}
            </div>

            {drill && (
                <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="family-drill">
                    <div className="flex items-center justify-between mb-4">
                        <div className="font-display text-2xl text-stone-900">{selected}&apos;s stuff</div>
                        <button className="text-[11px] text-stone-500 hover:text-stone-800" onClick={() => { setDrill(null); setSelected(null); }}>Close</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <div className="eyebrow mb-1">Assets ({drill.assets.length})</div>
                            <ul className="space-y-1 text-[13px]">
                                {drill.assets.slice(0, 8).map((a) => <li key={a.id} className="text-stone-700">{a.name} <span className="text-stone-400">· {fmtMoney(a.current_value ?? a.purchase_price)}</span></li>)}
                                {drill.assets.length === 0 && <li className="text-stone-400 text-[12px]">None</li>}
                            </ul>
                        </div>
                        <div>
                            <div className="eyebrow mb-1">Vehicles ({drill.vehicles.length})</div>
                            <ul className="space-y-1 text-[13px]">
                                {drill.vehicles.slice(0, 8).map((v) => <li key={v.id} className="text-stone-700">{v.name} <span className="text-stone-400">· {v.reg_number || "—"}</span></li>)}
                                {drill.vehicles.length === 0 && <li className="text-stone-400 text-[12px]">None</li>}
                            </ul>
                        </div>
                        <div>
                            <div className="eyebrow mb-1">Investments ({drill.investments.length})</div>
                            <ul className="space-y-1 text-[13px]">
                                {drill.investments.slice(0, 8).map((i) => <li key={i.id} className="text-stone-700">{i.name} <span className="text-stone-400">· {fmtMoney(i.current_value ?? i.invested_amount)}</span></li>)}
                                {drill.investments.length === 0 && <li className="text-stone-400 text-[12px]">None</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
